import { $vars } from '../../../config/vars';
import { yLoadAsyncScript } from '../../y/util';
import { $el } from '../elements';
import { UIView } from '../view';

let maps_promise: Promise<void> | undefined;

function initMapPopup() {
    return class MapPopup extends google.maps.OverlayView {
        private _position?: google.maps.LatLng;
        private _anchor: HTMLElement;

        constructor() {
            super();

            this._anchor = $el.DIV('ui-map-popup');

            // Optionally stop clicks, etc., from bubbling up to the map.
            this.stopEventPropagation();
        }

        set content(content: HTMLElement) {
            const anchor = this._anchor;
            anchor.innerHTML = '';
            anchor.appendChild(content);
        }

        override onAdd() {
            this.getPanes().overlayMouseTarget.appendChild(this._anchor);
        }

        override onRemove() {
            const anchor = this._anchor;

            if(anchor.parentElement)
                anchor.parentElement.removeChild(anchor);
        }

        override draw() {
            if(!this._position) return;

            const div_pos = this.getProjection().fromLatLngToDivPixel(this._position);
            const anchor = this._anchor;

            if(Math.abs(div_pos.x) < 4000 && Math.abs(div_pos.y) < 4000) {
                anchor.classList.remove('f-hidden');
                anchor.style.left = div_pos.x + 'px';
                anchor.style.top = div_pos.y + 'px';
            } else {
                anchor.classList.add('f-hidden');
            }
        }

        open(map: google.maps.Map, marker: google.maps.Marker) {
            this._position = marker.getPosition() || undefined;

            if(map != this.getMap())
                this.setMap(map);
            else
                this.draw();
        }

        stopEventPropagation() {
            const anchor = this._anchor;
            anchor.style.cursor = 'auto';

            ['click', 'dblclick', 'contextmenu', 'wheel', 'mousedown', 'touchstart', 'pointerdown'].forEach(e => {
                anchor.addEventListener(e, ev => {
                    ev.stopPropagation();
                });
            });
        }
    }
}

type ReturnType<T> = T extends (...args: any[]) => infer R ? R : T;

let MapPopup: ReturnType<typeof initMapPopup>;

function init() {
    if(!maps_promise) {
        const config = $vars.google_api;

        const script = '//maps.googleapis.com/maps/api/js?key=' + config.key + '&v=3.exp&use_slippy=true&callback=yInitMap';
        maps_promise = yLoadAsyncScript(script, 'yInitMap').then(() => {
            MapPopup = initMapPopup();
        });
    }

    return maps_promise;
}

export class UIMap extends UIView {
    map: Promise<google.maps.Map>;
    private _info_window?: google.maps.InfoWindow;
    private _popup_window?: typeof MapPopup.prototype;

    constructor(location: google.maps.LatLngLiteral, styles?: google.maps.MapTypeStyle[]) {
        super('div', 'ui-map');

        const div = $el.DIV('r-map');
        this.ui_add(div);

        this.map = init().then(() => {
            return new google.maps.Map(div, {
                zoom: 13,
                center: location,
                styles: styles || [
                    { stylers: [{ saturation: -80 }] },
                    { featureType: 'road.arterial' as any, stylers: [{ saturation: 50 }] },
                    { featureType: 'poi' as any, elementType: 'labels' as any, stylers: [{ visibility: 'off' }] }
                ],
                rotateControl: false,
                streetViewControl: false,
                backgroundColor: '#f7f7f7',
                fullscreenControl: false,
                mapTypeControl: false,
                minZoom: 12,
                zoomControlOptions: { position: google.maps.ControlPosition.TOP_RIGHT }
            });
        });
    }

    private _infoWindow(content: string | HTMLElement) {
        let window = this._info_window;
        if(!window) {
            window = this._info_window = new google.maps.InfoWindow({
                content: content,
            })
        } else {
            window.setContent(content);
        }

        return window;
    }

    private _popupWindow(content: HTMLElement | undefined) {
        let popup = this._popup_window;
        if(!popup) {
            popup = new MapPopup;
            this._popup_window = popup;
        }

        popup.content = content || $el.DIV();

        return popup;
    }

    addMarker(position: google.maps.LatLngLiteral, name: string, icon: string, popup?: () => HTMLElement | undefined) {
        this.map.then(map => {
            const marker = new google.maps.Marker({
                position: position,
                map: map,
                icon: { url: icon, size: new google.maps.Size(16, 16), scaledSize: new google.maps.Size(16, 16) },
                clickable: true,
            });

            marker.addListener('click', () => {
                if(popup) {
                    this._popupWindow(popup()).open(map, marker);
                } else {
                    this._infoWindow($el.SPAN(undefined, name)).open(map, marker);
                }
            });
        });
    }
}
